<template>
  <main class="mt-0 main-content auth-cover">
    <div class="auth-layout">
      <div class="auth-hero" :class="[imageClasses[currentImageIndex]]">
        <div class="auth-hero-overlay">
          <p class="auth-hero-kicker">Progress IMS</p>
          <h2 class="auth-hero-title">Business Account</h2>
          <p class="auth-hero-copy">{{ $t("loginHeroCopy") }}</p>
        </div>
      </div>
      <form class="auth-panel" action="?" method="POST" @submit.prevent="signIn">
        <button
          type="button"
          class="app-theme-toggle auth-theme-toggle"
          :aria-label="isDarkTheme ? $t('navThemeToLight') : $t('navThemeToDark')"
          :title="isDarkTheme ? $t('navThemeToLight') : $t('navThemeToDark')"
          @click="onToggleTheme"
        >
          <i :class="isDarkTheme ? 'bi bi-sun' : 'bi bi-moon'" aria-hidden="true"></i>
        </button>
        <div class="auth-panel-inner">
          <div class="auth-card">
            <div class="auth-intro">
              <div class="auth-lang">
                <button
                  v-for="lang in languages"
                  :key="lang.code"
                  type="button"
                  class="auth-lang-btn"
                  :class="{ active: currentLocale === lang.code }"
                  @click="setLanguage(lang.code)"
                >
                  <img :src="lang.flag" alt="" class="auth-lang-flag" />
                  <span>{{ lang.code.toUpperCase() }}</span>
                </button>
              </div>
              <h1 class="auth-title">{{ $t("loginTitle") }}</h1>
              <span class="auth-intro-accent" aria-hidden="true"></span>
              <p class="auth-subtitle">{{ $t("loginSubtitle") }}</p>
            </div>

            <div class="auth-form-field">
              <label class="form-label">{{ $t("loginUsername") }}</label>
              <el-input v-model="log" autocomplete="off" :placeholder="$t('loginUsernamePlaceholder')" size="large" />
            </div>
            <div class="auth-form-field">
              <label class="form-label">{{ $t("loginPassword") }}</label>
              <el-input
                v-model="password"
                autocomplete="off"
                type="password"
                :placeholder="$t('loginPasswordPlaceholder')"
                size="large"
                show-password
                @keyup.enter="signIn"
              />
            </div>
            <div class="auth-form-field auth-captcha">
              <div class="auth-captcha-widget">
                <vue-recaptcha
                  ref="vueRecaptcha"
                  :sitekey="recaptchaKey"
                  size="normal"
                  @verify="recaptchaVerified"
                >
                </vue-recaptcha>
              </div>
            </div>
            <input
              type="submit"
              :value="$t('loginSubmit')"
              class="mb-0 action-btn action-btn--info action-btn--md action-btn--block auth-submit"
              @click="signIn"
            />
            <div v-if="message != null" class="auth-error">
              <p class="mb-0 text-danger text-center text-xs">
                <i class="bi bi-exclamation-circle-fill"></i> {{ message }}
              </p>
            </div>

            <div class="auth-brand-mark">
              <img class="auth-brand-logo" :src="logo" alt="Progress IMS" />
              <span class="auth-brand-divider" aria-hidden="true"></span>
              <span class="auth-brand-badge">Business Account</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  </main>
</template>
<script>
import { getBrandLogo, recaptchaKey } from '@/project/Source/helper';
import Source from "../Source/claim.js";
import CryptoJS from "crypto-js";
import vueRecaptcha from 'vue3-recaptcha2';
import { resolveReturnPath } from '@/project/Source/routes';
import { changeLanguage } from '@/project/Source/lang/i18n';
import { getTheme, toggleTheme } from '@/project/Source/theme';
import azFlag from "@/assets/img/icons/flags/az.svg";
import enFlag from "@/assets/img/icons/flags/en.svg";
import ruFlag from "@/assets/img/icons/flags/ru.svg";
import trFlag from "@/assets/img/icons/flags/tr.svg";

export default {
  name: "SigninIllustration",
  components: {
    vueRecaptcha
  },
  data() {
    return {
      log: "",
      logo: getBrandLogo(),
      password: "",
      message: null,
      recaptchaKey,
      isCaptchaValid: false,
      loading: false,
      intervalId: null,
      theme: getTheme(),
      imageClasses: ['image1', 'image2', 'image3'],
      currentImageIndex: Math.floor(Math.random() * 3),
      languages: [
        { code: "az", flag: azFlag },
        { code: "en", flag: enFlag },
        { code: "ru", flag: ruFlag },
        { code: "tr", flag: trFlag },
      ],
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale || "az";
    },
    isDarkTheme() {
      return this.theme === "dark";
    },
  },
  created() {
    this.intervalId = setInterval(this.changeImage, 4000);
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  },
  methods: {
    onToggleTheme() {
      this.theme = toggleTheme();
    },
    setLanguage(code) {
      changeLanguage(code);
    },
    changeImage() {
      this.currentImageIndex = (this.currentImageIndex + 1) % this.imageClasses.length;
    },
    recaptchaVerified() {
      this.isCaptchaValid = true;
    },
    signIn() {
      if (!this.isCaptchaValid) {
        this.message = this.$t("loginCaptchaRequired");
        return;
      }

      this.loading = true;
      this.message = null;
      const data = {
        requestCorrelationId: "string",
        username: this.log,
        password: this.password,
      };
      Source.Login(data)
        .then((response) => {
          if (!response || !response.data) {
            this.message = this.$t("loginFailed");
            this.loading = false;
            return;
          }

          if (response.data.isSuccess) {
            window.localStorage.setItem(
              "jwt",
              CryptoJS.AES.encrypt(
                JSON.stringify(response.data.jwt),
                "secret_key"
              ).toString()
            );
            window.localStorage.setItem(
              "roles",
              CryptoJS.AES.encrypt(
                JSON.stringify(response.data.data[0].roles),
                "secret_key"
              ).toString()
            );
            window.localStorage.setItem(
              "user",
              CryptoJS.AES.encrypt(
                JSON.stringify(response.data.data[0].user),
                "secret_key"
              ).toString()
            );
            sessionStorage.setItem("policy", JSON.stringify("none"));

            var isMed = JSON.parse(
              CryptoJS.AES.decrypt(
                window.localStorage.getItem("roles"),
                "secret_key"
              ).toString(CryptoJS.enc.Utf8)
            ).some(function (obj) {
              return obj.roleId === 55;
            });

            if (!isMed) {
              this.message = this.$t("loginNoPermission");
              this.loading = false;
              return;
            }

            this.$router.push(resolveReturnPath(this.$route));
          } else {
            this.loading = false;
            this.message =
              (response.data.errors && response.data.errors[0]) ||
              this.$t("loginFailed");
          }
        })
        .catch(() => {
          this.loading = false;
          this.message = this.$t("loginFailed");
        });
    },
  },
};
</script>
<style scoped>
.auth-cover {
  margin: 0;
  min-height: 100vh;
  background: #f5f7fa;
}

.auth-layout {
  display: flex;
  min-height: 100vh;
}

.auth-hero {
  position: relative;
  flex: 1.35;
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  transition: background-image 1s ease-in-out;
}

.auth-hero-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 3rem;
  background: linear-gradient(
    180deg,
    rgba(10, 31, 61, 0.15) 0%,
    rgba(10, 31, 61, 0.78) 100%
  );
  color: #fff;
}

.auth-hero-kicker {
  margin: 0 0 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.85;
}

.auth-hero-title {
  margin: 0 0 0.5rem;
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.auth-hero-copy {
  margin: 0;
  max-width: 28rem;
  font-size: 1rem;
  line-height: 1.5;
  opacity: 0.9;
}

.image1 {
  background-image: url('~@/assets/img/logos/SignLogos/MedicalInsurance.jpg');
}

.image2 {
  background-image: url('~@/assets/img/logos/SignLogos/CarInsurance.jpg');
}

.image3 {
  background-image: url('~@/assets/img/logos/SignLogos/PropertyInsurance.jpg');
}

.auth-panel {
  position: relative;
  flex: 0 0 min(480px, 42vw);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2.5rem 2.25rem;
  background: var(--app-surface);
  border-left: 1px solid var(--app-border);
  overflow: hidden;
}

.auth-panel::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 4px;
  background: linear-gradient(
    90deg,
    var(--app-brand-deep) 0%,
    var(--bs-info) 42%,
    var(--bs-info-soft) 100%
  );
}

.auth-panel-inner {
  width: 100%;
  max-width: 380px;
}

.auth-card {
  width: 100%;
  padding: 0;
  background: transparent;
  box-shadow: none;
}

.auth-brand-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  margin-top: 2rem;
}

.auth-brand-logo {
  width: auto;
  height: 34px;
  max-width: 190px;
  object-fit: contain;
  flex: 0 0 auto;
}

.auth-brand-divider {
  display: block;
  width: 1px;
  height: 28px;
  background: var(--app-border-strong);
  flex: 0 0 auto;
}

.auth-brand-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.28rem 0.75rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--bs-info) 14%, var(--app-surface-muted));
  color: var(--bs-info);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  flex: 0 0 auto;
}

.auth-intro {
  width: 100%;
  margin-bottom: 1.75rem;
  text-align: center;
}

.auth-lang {
  display: inline-flex;
  gap: 0.35rem;
  margin-bottom: 1rem;
  padding: 0.25rem;
  border: 1px solid var(--app-border);
  border-radius: 999px;
  background: var(--app-surface-muted);
}

.auth-lang-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 2.4rem;
  padding: 0.28rem 0.55rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--app-text);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 0.18s ease;
}

.auth-lang-btn.active {
  background: var(--bs-info);
  color: #fff;
}

.auth-lang-flag {
  width: 16px;
  height: 11px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  display: inline-block;
  flex-shrink: 0;
}

.auth-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 750;
  letter-spacing: -0.03em;
  line-height: 1.15;
  color: var(--bs-info);
  background: linear-gradient(135deg, var(--app-brand-deep) 0%, var(--bs-info) 52%, var(--bs-info-soft) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.auth-intro-accent {
  display: block;
  width: 46px;
  height: 3px;
  margin: 0.65rem auto 0.7rem;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--app-brand-deep) 0%, var(--bs-info) 50%, var(--bs-info-soft) 100%);
}

.auth-subtitle {
  margin: 0;
  font-size: 0.94rem;
  font-weight: 500;
  color: var(--app-text);
  line-height: 1.45;
}

.auth-captcha {
  margin-top: 0.15rem;
  margin-bottom: 1rem;
}

.auth-captcha-widget {
  display: flex;
  justify-content: center;
  width: 100%;
}

.auth-submit {
  margin-top: 0.15rem;
  min-height: 46px;
  font-weight: 650;
  font-size: 1rem;
  border-radius: 10px !important;
  border-color: var(--bs-info) !important;
  background: var(--bs-info) !important;
  color: #ffffff !important;

  &:hover,
  &:focus,
  &:focus-visible {
    border-color: var(--bs-info-soft) !important;
    background: var(--bs-info-soft) !important;
    color: #ffffff !important;
  }
}

.auth-error {
  margin-top: 0.85rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

@media (max-width: 991.98px) {
  .auth-layout {
    flex-direction: column;
  }

  .auth-hero {
    flex: none;
    min-height: 220px;
  }

  .auth-hero-overlay {
    padding: 1.5rem;
  }

  .auth-hero-title {
    font-size: 1.45rem;
  }

  .auth-panel {
    flex: none;
    min-height: auto;
    border-left: 0;
    border-top: 1px solid #e5e7eb;
    padding: 2rem 1.25rem 2.5rem;
  }

  .auth-panel-inner {
    max-width: 420px;
  }
}
</style>
